import { ChartInfoKind } from '@frontend/chart/types';
import { EntityType } from '@frontend/common';
import { CommonMessage } from '@frontend/lang';
import { ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { PlatformObjectBadge } from '../../../modal/platform-object-badge/platform-object-badge.component';
import { StatisticsCard } from '../../shared';
import { UnavailableProductItem, useLendingUnavailableProductsListCard } from './use-lending-unavailable-products-list-card';

export interface LendingUnavailableProductsListCardProps {
    accountId: string;
}

export const LendingUnavailableProductsListCard = (props: LendingUnavailableProductsListCardProps) => {
    const { formatMessage } = useIntl();
    const { items, isLoading } = useLendingUnavailableProductsListCard(props);

    const unavailableItems = items.filter((item) => item.severity === 'unavailable');
    const almostItems = items.filter((item) => item.severity === 'almost');

    const availableHeader = formatMessage({
        id: 'platform.statistics.lending.lending-unavailable-products-list-card.available',
        description: 'Column header for available vs total equipment count on the lending unavailable equipments list',
        defaultMessage: 'Available'
    });

    return (
        <StatisticsCard
            infoKind={ChartInfoKind.UNAVAILABLE_PRODUCTS_LIST}
            title={
                <FormattedMessage
                    id='platform.statistics.lending.lending-unavailable-products-list-card.title'
                    description='Title of the lending dashboard list showing products with low equipment availability'
                    defaultMessage='Low availability'
                />
            }
            subtitle={
                <FormattedMessage
                    id='platform.statistics.lending.lending-unavailable-products-list-card.subtitle'
                    description='Subtitle of the lending dashboard list highlighting equipments with no or very few units left to lend'
                    defaultMessage='No or few units left'
                />
            }
            isLoading={isLoading}
            hasData={items.length > 0}>
            <div className='min-h-0 flex-1 overflow-y-auto'>
                <div className='border-border-default text-foreground-default flex items-start gap-4 border-b text-base font-medium'>
                    <div className='w-full px-4 py-3'>{CommonMessage.OBJECTS.EQUIPMENT.DEFAULT}</div>
                    <div className='w-[120px] shrink-0 px-4 py-3 text-right'>{availableHeader}</div>
                </div>
                {unavailableItems.length > 0 && (
                    <ProductSection
                        title={
                            <FormattedMessage
                                id='platform.statistics.lending.lending-unavailable-products-list-card.section-unavailable'
                                description='Section heading for equipments with zero available units on the lending dashboard'
                                defaultMessage='Completely unavailable'
                            />
                        }
                        items={unavailableItems}
                        badgeClassName='bg-status-danger'
                    />
                )}
                {almostItems.length > 0 && (
                    <ProductSection
                        title={
                            <FormattedMessage
                                id='platform.statistics.lending.lending-unavailable-products-list-card.section-almost'
                                description='Section heading for equipments with a low available-unit percentage on the lending dashboard'
                                defaultMessage='Almost unavailable'
                            />
                        }
                        items={almostItems}
                        badgeClassName='bg-status-warning'
                    />
                )}
            </div>
        </StatisticsCard>
    );
};

interface ProductSectionProps {
    title: ReactNode;
    items: UnavailableProductItem[];
    badgeClassName: string;
}

const ProductSection = (props: ProductSectionProps) => (
    <div>
        <div className='text-brand-muted px-4 py-2 text-sm font-semibold'>{props.title}</div>
        {props.items.map((item) => (
            <div
                key={item.product.id}
                className='border-border-default flex h-11 items-center gap-4 border-b'>
                <div className='text-foreground-default flex w-full min-w-0 items-center px-4 text-sm font-medium'>
                    <PlatformObjectBadge
                        display={item.product.name}
                        entityType={EntityType.PRODUCT}
                        id={item.product.id}
                    />
                </div>
                <div className='flex w-[120px] shrink-0 justify-end pe-4'>
                    <span
                        className={`text-background-surface inline-flex min-w-8 items-center justify-center rounded-full px-2 py-0.5 text-sm font-semibold ${props.badgeClassName}`}>
                        {item.product.available}/{item.total}
                    </span>
                </div>
            </div>
        ))}
    </div>
);
